import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { User } from '../my-type';
import { State, actionCreators } from '../state';
import { bindActionCreators } from 'redux';
import { isAllowedRiderTeamUser } from '../util/riderAccess';

interface UseAuthStatusOptions {
  redirectToLogin?: boolean;
  redirectPath?: string;
}

const useAuthStatus = (options: UseAuthStatusOptions = {}) => {
  const { redirectToLogin = false, redirectPath = '/login' } = options;
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const user: User | null = useSelector((state: State) => state.user);
  const dispatch = useDispatch();
  const { updateUser } = bindActionCreators(actionCreators, dispatch);
  const navigate = useNavigate();

  const checkAuthStatus = async (user: User | null) => {
    try {
      if (user && !isAllowedRiderTeamUser(user)) {
        updateUser(null);
        setLoggedIn(false);
        return;
      }

      if (user?.lineAccessToken) {
        if (new Date() > new Date(user.tokenExpiry || '1970-01-01')) {
          console.log('Stored user token expired');
          setLoggedIn(false);
          return;
        }
        // console.log('Stored user found, checking LINE accesstoken validity');
        // const isLineTokenValid = await checkLINEAccessTokenValidity(user.lineAccessToken);
        // setLoggedIn(isLineTokenValid);
        // console.log(isLineTokenValid ? 'LINE accesstoken valid' : 'LINE accesstoken expired');
        setLoggedIn(true);
      } else if (user?.jwtToken) {
        setLoggedIn(true);
      } else {
        console.log('No stored user or lineAccessToken');
        setLoggedIn(false);
      }
    } catch (error) {
      console.error('Failed to check auth status', error);
      setLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true); // Reset loading state on user change
    checkAuthStatus(user);
  }, [user]);

  // Handle redirect logic when authentication fails
  useEffect(() => {
    if (!loading && !loggedIn && redirectToLogin) {
      const currentLocation = window.location.pathname;
      window.location.href = `${redirectPath}?location=${currentLocation}`;
    }
  }, [loading, loggedIn, redirectToLogin, redirectPath, navigate]);

  const authStatus = useMemo(() => ({ loggedIn, loading, user }), [loggedIn, loading, user]);
  return authStatus;
};

export default useAuthStatus;
